import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule, DOCUMENT, ViewportScroller } from '@angular/common';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import { TransactionsPreviewDialogComponent, TransactionsPreviewDialogResult } from '@lmc-app/loan-servicing/payment-processing/create/transactions-preview-dialog.component';
import { AchUploadDialogComponent, AchUploadDialogResult } from '@lmc-app/loan-servicing/payment-processing/create/ach-upload-dialog.component';
import { ManualTransactionDialogComponent, ManualTransactionDialogResult } from '@lmc-app/loan-servicing/payment-processing/create/manual-transaction-dialog.component';
import { SanityCheckDialogComponent } from '@lmc-app/loan-servicing/payment-processing/sanity-check/sanity-check-dialog.component';
import { FUNDING_TYPE_OPTIONS, fundingTypeLabel } from '@lmc-app/loan-servicing/payment-processing/funding-type.constant';
import {
  KapchargeBatch,
  KapchargeBatchResponse,
  KapchargeBatchSearchParams,
  PaymentProcessingService,
} from '@lmc-app/core/services/payment-processing.service';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import { CUSTOM_DATE_FORMAT } from '@lmc-app/shared/constants/common.constant';
import { LmcDialogComponent } from '@lmc-app/shared/components/dialog/lmc-dialog.component';
import { environment } from '@lmc-env/environment';
import { LmcLoadingDialogComponent } from '@lmc-app/shared/components/dialog/lmc-loading-dialog.component';
import {
  StatusBreakdownComponent,
  StatusBreakdownItem,
} from '@lmc-app/shared/components/status-breakdown/status-breakdown.component';
import { kapchargeStatusItems } from '@lmc-app/loan-servicing/payment-processing/kapcharge-status-breakdown.constant';
import { TransactionSearchDialogComponent } from '@lmc-app/loan-servicing/payment-processing/search/transaction-search-dialog.component';

interface FilterModel {
  createdDateFrom: Date | null;
  createdDateTo: Date | null;
  fundingType: string | null;
}

/**
 * This component's own route. The batch page is its child, so the two are told apart by
 * comparing a pending navigation against this path (see pendingPath).
 */
const LIST_PATH = '/loan-servicing/payment-processing';

/** Gap between due-date chips; mirrors `.bc-due-dates { gap: 0.25rem }`. */
const CHIP_GAP_PX = 4;

/** Width reserved for the "+N" indicator when some due dates don't fit. */
const MORE_INDICATOR_WIDTH_PX = 30;

@Component({
  selector: 'app-transaction-list',
  templateUrl: './transaction-list.component.html',
  styleUrls: ['./transaction-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyMaterialModule,
    FormlyMatDatepickerModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTooltipModule,
    StatusBreakdownComponent,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
  ],
})
export class TransactionListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  /** Due-date cells, measured to decide how many chips fit on one line. */
  @ViewChildren('dueValue') private dueValueCells!: QueryList<ElementRef<HTMLElement>>;

  /**
   * Due dates shown inline on a card; the rest collapse into a "+N" tooltip. Starts at
   * one and is widened to whatever the rendered cell can hold (see recomputeDueDateFit).
   */
  private dueDateVisibleLimit = 1;

  readonly FormatUtil = FormatUtil;

  /** Shared code -> label resolver used by the funding-type column. */
  readonly fundingTypeLabel = fundingTypeLabel;

  /** Clear (x) button rendered as a matSuffix on every filter field (see template). */
  @ViewChild('clearSuffix', { static: true }) clearSuffix!: TemplateRef<unknown>;

  /**
   * Maps a batch row to the item list consumed by <lmc-status-breakdown>. The
   * palette is shared with the batch detail page via kapchargeStatusItems so
   * status colors and ordering stay consistent across the feature.
   */
  statusItems(row: KapchargeBatch): StatusBreakdownItem[] {
    return kapchargeStatusItems(row);
  }

  /**
   * Due dates are already date-only strings (YYYY-MM-DD) from the API, so they are
   * rendered as-is — no timezone-sensitive reformatting needed.
   */
  visibleDueDates(row: KapchargeBatch): string[] {
    return (row.dueDates ?? []).slice(0, this.dueDateVisibleLimit);
  }

  /** Number of due dates hidden behind the "+N" chip (0 when all fit). */
  hiddenDueDateCount(row: KapchargeBatch): number {
    return Math.max((row.dueDates?.length ?? 0) - this.dueDateVisibleLimit, 0);
  }

  /** The batch's creation date — the only date kept for a batch. */
  batchDate(row: KapchargeBatch): string {
    return row.createdAt ? FormatUtil.formatDate(row.createdAt, 'YYYY-MM-DD') : '—';
  }

  /** Badge modifier for the batch's file type ('EFT' when the batch has none). */
  fileTypeBadgeClass(fileType: string | null): string {
    return `badge-${(fileType ?? 'EFT').toLowerCase()}`;
  }

  trackByBatchId(_index: number, row: KapchargeBatch): string {
    return row.batchId;
  }

  // ---------------------------------------------------------------------------
  // Filter form (ngx-formly)
  // ---------------------------------------------------------------------------

  readonly filterForm = new FormGroup({});
  filterModel: FilterModel = { createdDateFrom: null, createdDateTo: null, fundingType: null };
  filterFields: FormlyFieldConfig[] = [];

  batches: KapchargeBatch[] = [];
  isLoading = false;
  totalRows = 0;
  pageSize = 20;
  currentPage = 0;

  private readonly destroy$ = new Subject<void>();
  private dueDateResizeObserver?: ResizeObserver;

  /**
   * True while the batch page is showing. The list is hidden rather than destroyed, which is
   * what preserves its filters, page, rows and scroll offset (see onChildActivate).
   */
  childOpen = false;

  /** Where the operator was looking when they opened a batch. */
  private savedScrollPosition: [number, number] = [0, 0];


  constructor(
    private readonly paymentProcessingService: PaymentProcessingService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly zone: NgZone,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly viewportScroller: ViewportScroller,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnInit(): void {
    this.filterFields = this.buildFilterFields();

    // A batch URL opened cold activates this list too (it is the batch page's parent), and
    // the list behind an open batch is not worth a query — onChildDeactivate loads it when
    // the operator closes the batch. Should firstChild ever read empty here, the only cost
    // is that one extra query, not a broken page.
    if (!this.route.firstChild) {
      this.loadGroups();
    }
  }

  /**
   * Moves the window with no animation.
   *
   * Bootstrap's Reboot ships `:root { scroll-behavior: smooth }` — under
   * `@media (prefers-reduced-motion: no-preference)`, so it is invisible to anyone browsing
   * with reduced motion on — and it applies to the document scroller. Every `window.scrollTo`
   * in the app therefore animates, which made closing a batch visibly scroll back down to
   * where the operator had been instead of simply being there.
   *
   * An inline `scroll-behavior: auto` on <html> outranks the stylesheet for the duration of
   * the call and is put back straight after; with the behaviour auto, `window.scrollTo` is
   * synchronous, so there is nothing in flight when the previous value is restored.
   *
   * Preferred over `behavior: 'instant'` in ScrollToOptions: that enum value is a recent
   * addition and a browser that does not know it throws on the call rather than ignoring it.
   */
  private scrollInstantlyTo(position: [number, number]): void {
    const root = this.document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    this.viewportScroller.scrollToPosition(position);
    root.style.scrollBehavior = previous;
  }

  /**
   * The batch page is opening: remember the scroll offset, hide the list, and start the batch
   * at the top.
   *
   * The offset is read here rather than in onRowClick so every way into a batch is covered — a
   * row click, the browser's Forward button, a pasted URL. The child's view is in the DOM by
   * now but the list is still laid out (childOpen only takes effect on the next change
   * detection), so the window is still where the operator left it.
   *
   * Scrolling to the top is not cosmetic. Hiding the list collapses the document to the batch
   * page's own height, and the browser clamps the window offset to whatever is left — so a
   * batch opened from far down the list would render already scrolled. It is done *before*
   * the hide, so there is no clamp to fight and no need to force a change-detection pass over
   * the child that was just created. Nothing paints between the two, so there is no jump —
   * and it goes through scrollInstantlyTo, or the trip to the top would animate.
   */
  onChildActivate(): void {
    this.savedScrollPosition = this.viewportScroller.getScrollPosition();
    this.scrollInstantlyTo([0, 0]);
    this.childOpen = true;
  }

  /**
   * The batch page is closing. Show the list again, put it back where it was, and refresh it.
   *
   * The scroll is restored synchronously, right after detectChanges() has un-hidden the list:
   * the rows never left the DOM, so the document is already tall enough to hold the offset.
   * Waiting a tick would show the list at the wrong offset for one frame, and it has to go
   * through scrollInstantlyTo or the list visibly scrolls itself back down.
   *
   * The refresh follows because a batch page can create a new batch (a refund does), and the
   * rows it replaces are the same count and shape, so it does not disturb the offset just set.
   */
  onChildDeactivate(): void {
    this.childOpen = false;

    // Leaving the feature entirely (the top nav, a link out) also deactivates this outlet,
    // and this list is torn down with it. Scrolling the window then would hand the next page
    // a scrolled viewport, and the refresh would be a request for nobody.
    if (this.pendingPath !== LIST_PATH) return;

    this.changeDetectorRef.detectChanges();
    this.scrollInstantlyTo(this.savedScrollPosition);
    this.loadGroups();
  }

  /**
   * Path the in-flight navigation is heading to, without its query string or fragment, or
   * null outside a navigation. Used to tell "the batch page closed" apart from "the operator
   * left Payment Processing", which reach onChildDeactivate the same way.
   */
  private get pendingPath(): string | null {
    const target = this.router.getCurrentNavigation()?.finalUrl;
    return target ? this.router.serializeUrl(target).split(/[?#]/)[0] : null;
  }

  /**
   * Formly field config for the filter row. Built here (not inline) so each field can
   * reference the shared `clearSuffix` template (a @ViewChild resolved before ngOnInit).
   * The datepicker toggle is placed on the prefix so the clear (x) button owns the suffix
   * slot on every field, keeping the clear button in a consistent right-hand position.
   */
  private buildFilterFields(): FormlyFieldConfig[] {
    return [
      {
        fieldGroupClassName: 'filter-grid',
        fieldGroup: [
          {
            key: 'createdDateFrom',
            type: 'datepicker',
            props: {
              label: 'Created Date From',
              placeholder: 'Select date',
              appearance: 'outline',
              suffix: this.clearSuffix,
              datepickerOptions: { datepickerTogglePosition: 'prefix' },
            },
          },
          {
            key: 'createdDateTo',
            type: 'datepicker',
            props: {
              label: 'Created Date To',
              placeholder: 'Select date',
              appearance: 'outline',
              suffix: this.clearSuffix,
              datepickerOptions: { datepickerTogglePosition: 'prefix' },
            },
          },
          {
            key: 'fundingType',
            type: 'select',
            props: {
              label: 'Funding Type',
              placeholder: 'All funding types',
              appearance: 'outline',
              suffix: this.clearSuffix,
              options: FUNDING_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
            },
          },
        ],
      },
    ];
  }

  ngAfterViewInit(): void {
    // Re-attach whenever the rendered cards change (new page, new filter result), since
    // the observed cell belongs to a card that *ngFor may have destroyed.
    this.dueValueCells.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.observeDueDateCell());
    this.observeDueDateCell();
  }

  ngOnDestroy(): void {
    this.dueDateResizeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Watches one due-date cell so the chip count can follow its real width. Every card's
   * cell sits in the same grid track, so measuring a single one covers the whole list —
   * and the track is sized by the grid, never by these chips, so writing the new count
   * back cannot feed into the next measurement.
   */
  private observeDueDateCell(): void {
    this.dueDateResizeObserver?.disconnect();

    // A cell only measures if it actually rendered a chip.
    const cell = this.dueValueCells.find((c) => !!c.nativeElement.querySelector('.date-chip'));
    if (!cell) return;

    this.dueDateResizeObserver = new ResizeObserver(() =>
      this.recomputeDueDateFit(cell.nativeElement),
    );
    // Observing fires the callback once, which seeds the initial count.
    this.dueDateResizeObserver.observe(cell.nativeElement);
  }

  /**
   * Fits as many due-date chips as the cell's width allows. Chips are uniform (every
   * date is YYYY-MM-DD), so one rendered chip gives the per-chip width.
   */
  private recomputeDueDateFit(cell: HTMLElement): void {
    const chip = cell.querySelector<HTMLElement>('.date-chip');
    if (!chip) return;

    const chipWidth = chip.offsetWidth + CHIP_GAP_PX;
    const available = cell.clientWidth + CHIP_GAP_PX;
    if (chipWidth <= CHIP_GAP_PX || available <= 0) return;

    let fit = Math.max(1, Math.floor(available / chipWidth));
    // Only give up room for the "+N" indicator when something is actually hidden.
    if (this.maxDueDateCount > fit) {
      fit = Math.max(1, Math.floor((available - MORE_INDICATOR_WIDTH_PX) / chipWidth));
    }
    if (fit === this.dueDateVisibleLimit) return;

    // ResizeObserver is not patched by zone.js (that needs zone-patch-resize-observer),
    // so the callback lands outside the Angular zone and would not trigger a check.
    this.zone.run(() => {
      this.dueDateVisibleLimit = fit;
    });
  }

  /** Longest due-date list on the current page; decides if a "+N" chip is needed. */
  private get maxDueDateCount(): number {
    return this.batches.reduce((max, b) => Math.max(max, b.dueDates?.length ?? 0), 0);
  }

  /**
   * Opens the read-only sanity check: compares each active loan's latest scheduled
   * payment in a due-date window against its amortized payment amount. Changes nothing,
   * so the list does not need refreshing when it closes.
   */
  onRunSanityCheck(): void {
    this.dialog.open(SanityCheckDialogComponent, {
      width: '95vw',
      maxWidth: '1200px',
      maxHeight: '90vh',
      autoFocus: false,
    });
  }

  onCreate(): void {
    this.fileInput.nativeElement.value = '';
    this.fileInput.nativeElement.click();
  }

  onCreateManual(): void {
    const dialogRef = this.dialog.open(ManualTransactionDialogComponent, {
      width: '95vw',
      maxWidth: '1400px',
      height: '90vh',
      autoFocus: 'first-heading',
      disableClose: true,
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: ManualTransactionDialogResult) => {
      if (result === 'submitted') {
        this.snackBar.open('Manual transaction created successfully.', 'Close', {
          duration: 5000,
          panelClass: 'snackbar-success',
        });
        this.loadGroups();
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();

    if (ext === 'ach') {
      const dialogRef = this.dialog.open(AchUploadDialogComponent, {
        width: '460px',
        disableClose: true,
      });
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: AchUploadDialogResult | undefined) => {
        if (!result) return;
        this.processFileUpload(file, result.originatorType);
      });
    } else {
      this.processFileUpload(file);
    }
  }

  private processFileUpload(file: File, originatorType?: string): void {
    const loadingRef = this.dialog.open(LmcLoadingDialogComponent, {
      disableClose: true,
      data: { message: 'Uploading and processing file, please wait...' },
    });

    this.paymentProcessingService
      .uploadFile(file, originatorType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          loadingRef.close();
          const ref = this.dialog.open(TransactionsPreviewDialogComponent, {
            width: '95vw',
            maxWidth: '1400px',
            maxHeight: '90vh',
            autoFocus: false,
            disableClose: true,
            data: { parsedFile: response.parsedFile as any, lookupResults: response.lookupResults },
          });
          ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: TransactionsPreviewDialogResult) => {
            if (result === 'submitted') {
              this.snackBar.open('Payments submitted.', 'Close', {
                duration: 5000,
                panelClass: 'snackbar-success',
              });
              this.loadGroups();
            }
          });
        },
        error: (err) => {
          loadingRef.close();
          this.openErrorDialog('Upload Failed', err?.error?.message ?? err?.message ?? 'Failed to process file. Please try again.');
        },
      });
  }

  private openErrorDialog(title: string, content: string): void {
    this.dialog.open(LmcDialogComponent, {
      data: { title, content, positiveButtonText: 'OK' },
    });
  }

  onSearch(): void {
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadGroups();
  }

  onReset(): void {
    this.filterForm.reset();
    this.filterModel = { createdDateFrom: null, createdDateTo: null, fundingType: null };
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadGroups();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadGroups();
  }

  /**
   * Opens the batch's own page. Batch details used to be a dialog; it is a child route now
   * (`./:batchId`), so the operator gets a URL they can refresh, bookmark and share. The back
   * button in that page's header is their way back here.
   *
   * Nothing is handed over: the batch page renders into this component's own outlet, so this
   * list stays mounted behind it and keeps its filters, page, rows and scroll offset the way
   * it did under a dialog. See onChildActivate / onChildDeactivate.
   */
  onRowClick(row: KapchargeBatch): void {
    this.router.navigate([row.batchId], { relativeTo: this.route });
  }

  private buildSearchParams(page: number, pageSize: number): KapchargeBatchSearchParams {
    const form = this.filterModel;
    const params: KapchargeBatchSearchParams = { page, pageSize };

    if (form.createdDateFrom) {
      params.createdDateFrom = dayjs(form.createdDateFrom).startOf('day').toISOString();
    }
    if (form.createdDateTo) {
      params.createdDateTo = dayjs(form.createdDateTo).endOf('day').toISOString();
    }
    if (form.fundingType) {
      params.fundingType = form.fundingType;
    }

    return params;
  }

  loadGroups(): void {
    this.isLoading = true;

    this.paymentProcessingService
      .getTransactionGroups(this.buildSearchParams(this.currentPage + 1, this.pageSize))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: KapchargeBatchResponse) => {
          this.batches = response.results ?? [];
          this.totalRows = response.rowCount ?? 0;
          this.isLoading = false;
        },
        error: () => {
          this.batches = [];
          this.totalRows = 0;
          this.isLoading = false;
        },
      });
  }

  /**
   * Opens the global transaction search.
   *
   * The search is a dialog rather than a field on this page because what it returns are
   * transactions, and this page is a list of batches — there is nowhere on it for a
   * transaction to go. It closes with the batch id of the row the operator picked, and
   * that batch is opened here, through the same child route a card click uses.
   */
  onSearchTransactions(): void {
    const dialogRef = this.dialog.open(TransactionSearchDialogComponent, {
      width: '95vw',
      maxWidth: '1200px',
      // autoFocus is left at its default so the focus trap honours the [cdkFocusInitial]
      // on the search input — the operator opens this dialog to type.
      restoreFocus: true,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((batchId: string | undefined) => {
        if (batchId) {
          this.router.navigate([batchId], { relativeTo: this.route });
        }
      });
  }
}
